import { isTaxonomyFilterId } from '../constants/filter-constants';
import {
  type Filter,
  FilterId,
  type LinkedTaxonomyFilter,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import type { TaxonomyFilterRemovalEvent } from './trackTaxonomyFiltersRemoved';
import { validateActivityFilters } from './validateActivityFilters';
import { validateGeographyFilters } from './validateGeographyFilters';
import { validateRoleFilters } from './validateRoleFilters';
import { validateSkillFilters } from './validateSkillFilters';

const markLinkAppliedTaxonomies = (
  filters: [FilterId, Filter][]
): [FilterId, Filter][] =>
  filters.map(([id, filter]) => {
    const { isAdaptive, ...rest } = filter as LinkedTaxonomyFilter;
    if (!isTaxonomyFilterId(id) || typeof isAdaptive !== 'boolean') {
      return [id, filter];
    }
    return [
      id,
      { ...rest, isUserSubmitted: !isAdaptive, isLinkApplied: true } as Filter,
    ];
  });

export const validateTaxonomyFilters = ({
  filters,
  taxonomyConfig,
  showTaxonomyVersionToast,
  onTaxonomyFiltersRemoved,
}: {
  filters: [FilterId, Filter][];
  taxonomyConfig: TaxonomyConfig;
  showTaxonomyVersionToast?: (
    toastId: string,
    filterId: FilterId,
    title?: string
  ) => void;
  onTaxonomyFiltersRemoved?: (removal: TaxonomyFilterRemovalEvent) => void;
}): [FilterId, Filter][] => {
  const { skillTaxonomyVersion, roleTaxonomyVersion, activityTaxonomyVersion } =
    taxonomyConfig;

  const skillValidated = validateSkillFilters({
    filters,
    taxonomyVersion: skillTaxonomyVersion,
    onRemoveFilter: (_, removal) => {
      showTaxonomyVersionToast?.('skill-filters-removed', FilterId.SKILL);
      onTaxonomyFiltersRemoved?.({ taxonomy: 'skill', ...removal });
    },
  });

  const activityValidated = validateActivityFilters({
    filters: skillValidated,
    taxonomyVersion: activityTaxonomyVersion,
    onRemoveFilter: (_, removal) => {
      showTaxonomyVersionToast?.('activity-filters-removed', FilterId.ACTIVITY);
      onTaxonomyFiltersRemoved?.({ taxonomy: 'activity', ...removal });
    },
  });

  const roleValidated = validateRoleFilters({
    filters: activityValidated,
    taxonomyVersion: roleTaxonomyVersion,
    onRemoveFilter: (_, removal) => {
      showTaxonomyVersionToast?.('role-filters-removed', FilterId.ROLE);
      onTaxonomyFiltersRemoved?.({ taxonomy: 'role', ...removal });
    },
  });

  const geographyValidated = validateGeographyFilters({
    filters: roleValidated,
    onRemoveFilter: (_, removal) => {
      onTaxonomyFiltersRemoved?.({ taxonomy: 'geography', ...removal });
      showTaxonomyVersionToast?.(
        'geography-taxonomy-unavailable',
        FilterId.GEOGRAPHY,
        'This link uses a geography taxonomy that is no longer available. Showing your own geography setting instead.'
      );
    },
  });

  return markLinkAppliedTaxonomies(geographyValidated);
};
